import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import GetLocalStorageUserId from "../../sharedComponents/GetLocalStorageuserId";
import { isAuthenticationError } from "../../../utils/authToken";
import {
  getCarrierPartnerPoints,
  createCarrierPartnerPoint,
  updateCarrierPartnerPoint,
  deleteCarrierPartnerPoint,
} from "../../../api/carrierPartnerApi";

const getAdminUserId = () => {
  try {
    return String(GetLocalStorageUserId() || "");
  } catch {
    return "";
  }
};

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.title || data?.message || err?.message || fallback;
};

const toastApiError = (err, fallback) => {
  if (isAuthenticationError(err)) return;
  toast.error(apiError(err, fallback));
};

const pickCreatedPoint = (data, name) => {
  if (!data || typeof data !== "object") return null;
  const id = data.id ?? data.Id;
  if (id == null || id === "") return null;
  return {
    id: Number(id),
    name: data.name || data.Name || name,
    isActive: data.isActive ?? data.IsActive ?? true,
  };
};

const CarrierPartnerPointManager = ({ onChanged, compact = false }) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const fetchPoints = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCarrierPartnerPoints();
      setPoints(data);
      if (typeof onChangedRef.current === "function") onChangedRef.current(data);
      return data;
    } catch (err) {
      toastApiError(err, "Failed to load point names.");
      setPoints([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  const handleAdd = async () => {
    const adminId = getAdminUserId();
    const name = String(nameInput || "").trim();
    if (!name) {
      toast.error("Enter a point name.");
      return;
    }
    setSaving(true);
    try {
      const created = await createCarrierPartnerPoint({
        name,
        creatorId: adminId,
      });
      toast.success("Point name added.");
      setNameInput("");
      const list = await fetchPoints();
      const row = pickCreatedPoint(created, name);
      if (row && typeof onChangedRef.current === "function") {
        onChangedRef.current(list, row);
      }
    } catch (err) {
      toastApiError(err, "Failed to add point name.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveName = async (point) => {
    const name = String(editingName || "").trim();
    if (!name) {
      toast.error("Point name is required.");
      return;
    }
    setSaving(true);
    try {
      await updateCarrierPartnerPoint(point.id, {
        id: point.id,
        name,
        isActive: point.isActive,
        modifierId: getAdminUserId(),
      });
      toast.success("Point name updated.");
      setEditingId(null);
      setEditingName("");
      await fetchPoints();
    } catch (err) {
      toastApiError(err, "Failed to update point name.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (point) => {
    const confirm = await Swal.fire({
      title: "Deactivate point name?",
      text: `"${point.name}" will no longer appear in the dropdown.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Deactivate",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteCarrierPartnerPoint(point.id, getAdminUserId());
      toast.success("Point name deactivated.");
      await fetchPoints();
    } catch (err) {
      toastApiError(err, "Failed to deactivate point name.");
    }
  };

  const handleReactivate = async (point) => {
    try {
      await updateCarrierPartnerPoint(point.id, {
        id: point.id,
        name: point.name,
        isActive: true,
        modifierId: getAdminUserId(),
      });
      toast.success("Point name reactivated.");
      await fetchPoints();
    } catch (err) {
      toastApiError(err, "Failed to reactivate point name.");
    }
  };

  return (
    <div className={compact ? "" : "rounded-xl border border-stone-200 bg-stone-50 p-4"}>
      <p className="text-xs text-stone-600 mb-3">
        These describe what the carrier is doing, e.g. UK to BD or customer home
        to UK hub.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Add a point name"
          className="flex-1 h-10 px-3 rounded-lg border-2 border-stone-200 outline-none bg-white text-sm font-medium"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving}
          className="px-4 h-10 rounded-lg bg-stone-900 text-white font-semibold hover:bg-stone-800 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Add"}
        </button>
      </div>
      {loading && points.length === 0 ? (
        <p className="text-sm text-stone-500">Loading point names…</p>
      ) : points.length === 0 ? (
        <p className="text-sm text-stone-500">No point names yet.</p>
      ) : (
        <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto">
          {points.map((point) => (
            <li
              key={point.id}
              className="flex flex-wrap items-center gap-2 rounded-lg bg-white border border-stone-200 px-3 py-2"
            >
              {editingId === point.id ? (
                <>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 min-w-[10rem] h-9 px-2 rounded-md border border-stone-300 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveName(point)}
                    disabled={saving}
                    className="text-sm font-semibold text-stone-800 hover:underline"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setEditingName("");
                    }}
                    className="text-sm font-semibold text-stone-500 hover:underline"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span
                    className={`flex-1 min-w-[10rem] text-sm font-medium ${
                      point.isActive
                        ? "text-stone-900"
                        : "text-stone-400 line-through"
                    }`}
                  >
                    {point.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(point.id);
                      setEditingName(point.name);
                    }}
                    className="text-sm font-semibold text-stone-700 hover:underline"
                  >
                    Rename
                  </button>
                  {point.isActive ? (
                    <button
                      type="button"
                      onClick={() => handleDeactivate(point)}
                      className="text-sm font-semibold text-red-700 hover:underline"
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleReactivate(point)}
                      className="text-sm font-semibold text-emerald-700 hover:underline"
                    >
                      Reactivate
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CarrierPartnerPointManager;
